import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Search, Sparkles, ArrowRight, HelpCircle } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import CredibilityStrip from "./CredibilityStrip";
import SEO from "../SEO";
import FaqList from "../FaqList";
import { Reveal } from "../Reveal";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { FAQ_SEGMENTS } from "../../data/faqs";
import { PRODUCTS_KB } from "../../data/knowledge";
import { FEATURE_CATEGORIES, featurePath } from "../../data/features";

// FAQ segment -> the product page that is the authoritative source for it.
const SEGMENT_SOURCE = Object.fromEntries(
  PRODUCTS_KB.flatMap((p) => {
    const f = FEATURE_CATEGORIES.find((c) => c.id === p.featureId);
    return f ? p.faqSegments.map((seg) => [seg, { name: p.name, path: featurePath(f) }]) : [];
  })
);
SEGMENT_SOURCE.pricing = { name: "Pricing", path: "/pricing" };
SEGMENT_SOURCE.general = { name: "About MagicBolt AI", path: "/about" };
SEGMENT_SOURCE.policies = { name: "Refund Policy", path: "/refund-policy" };
import { SIGNUP_URL } from "../../data/content";

const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_SEGMENTS.flatMap((s) =>
    s.items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    }))
  ),
};

export default function FaqPage() {
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();

  const segments = useMemo(() => {
    if (!q) return FAQ_SEGMENTS;
    return FAQ_SEGMENTS.map((s) => ({
      ...s,
      items: s.items.filter(
        (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
      ),
    })).filter((s) => s.items.length > 0);
  }, [q]);

  const total = FAQ_SEGMENTS.reduce((n, s) => n + s.items.length, 0);
  const shown = segments.reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="faq-page">
      <SEO
        title="FAQ - MagicBolt AI | Answers by Product"
        description="Answers to common questions about MagicBolt AI - Google Business Profile, Facebook, Instagram, WordPress automation, white-label, pricing and more."
        path="/faq"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "FAQ", path: "/faq" },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSON_LD)}</script>
      </Helmet>
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-14 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="mb-blob mb-blob-blue mb-anim-b w-[300px] h-[300px] top-10 -left-20" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">{total} answers, by product</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              Frequently asked{" "}
              <span className="mb-gradient-text">questions</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
              Everything about MagicBolt AI - segmented by product so you can jump straight
              to what matters.
            </p>

            {/* Search */}
            <div className="relative mt-8 max-w-lg mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                data-testid="faq-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions… e.g. reviews, Instagram, billing"
                className="h-12 pl-11 pr-4 border-[#E6E9F1] bg-white focus-visible:ring-[#FF6B00] text-[#0B1533]"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Segment quick-nav */}
      {!q && (
        <div className="sticky top-16 z-30 bg-[#F4F6FB]/85 backdrop-blur-md border-b border-[#E6E9F1]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
            {FAQ_SEGMENTS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid="faq-nav-chip"
                className="whitespace-nowrap rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 text-xs font-medium text-[#475569] hover:border-[#FF6B00]/40 hover:text-[#0B1533] transition-colors"
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Segments */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-8">
          {q && (
            <p className="mb-8 text-sm text-[#475569]" data-testid="faq-search-count">
              Showing <span className="font-semibold text-[#0B1533]">{shown}</span> result{shown === 1 ? "" : "s"} for “{query}”
            </p>
          )}

          {segments.length === 0 && (
            <div className="text-center py-16" data-testid="faq-no-results">
              <HelpCircle className="w-10 h-10 text-[#CBD2E0] mx-auto" />
              <p className="mt-4 text-[#475569]">No questions match “{query}”. Try another keyword.</p>
            </div>
          )}

          <div className="space-y-14">
            {segments.map((seg) => (
              <div key={seg.id} id={seg.id} data-testid="faq-segment" className="scroll-mt-32">
                <Reveal>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="h-6 w-1.5 rounded-full bg-[#FF6B00]" />
                    <h2 className="inline-block uppercase text-xs font-semibold tracking-[0.2em] text-[#FF6B00]">
                      {seg.title}
                    </h2>
                  </div>
                </Reveal>
                <Reveal delay={0.05}>
                  <FaqList items={seg.items} group={`faq-${seg.id}`} variant="white" />
                  {SEGMENT_SOURCE[seg.id] && (
                    <Link
                      to={SEGMENT_SOURCE[seg.id].path}
                      className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline"
                    >
                      Learn more: {SEGMENT_SOURCE[seg.id].name} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-[#E6E9F1] overflow-hidden bg-white">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[340px] h-[340px] -top-20 left-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 mb-radial-glow" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              Still have questions?
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#475569] max-w-xl mx-auto leading-relaxed">
              Start your free trial and see MagicBolt AI in action, or compare plans to find your fit.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7 text-base hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(255,107,0,0.35)]" asChild>
                <a href={SIGNUP_URL}>Start Free Trial <ArrowRight className="ml-2 w-4 h-4" /></a>
              </Button>
              <Button size="lg" variant="outline" className="border-[#CBD2E0] bg-white text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-lg h-12 px-7 text-base" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <CredibilityStrip />

      <Footer />
    </div>
  );
}
